// @ts-expect-error Missing types
import jsreport from '@jsreport/browser-client';
import { type SILogReport } from '@/reports/models/report';
import { type ReportServerConfig } from '@/reports/models/report';
import VehicleMoveReportFilter from './VehicleMoveReportFilter.vue';
import { ReportType } from 'shared/reports/domain/reportType';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useHttpClient } from '@/common/api/httpClient';
import { useDarkMode } from 'shared/themes/composables/darkMode';
import { useThemeStore } from 'shared/themes/stores/themeStore';

interface ReportData {
  darkMode: boolean;
  tiYardTheme: boolean;
  totalMoves: number;
  totalLogs: number;
  totalVolume: string;
  totalTime: number;
  moves: ReportDataMoves[];
}

interface ReportDataMoves {
  from: string;
  pickupDateTime: string;
  to: string;
  dropOffDateTime: string;
  products: string;
  noOfLogs: number;
  volume: string;
  timeSpent: number;
}

interface VehicleMoveCycle {
  fromStorageAreaName: string;
  fromServerTimestamp: string;
  toStorageAreaName: string;
  toServerTimestamp: string;
  products: string;
  numberOfLogs: number;
  volume: number;
}

interface LogYardVehicleMovesRequest {
  from: string;
  to: string;
  vehicleId: string;
}

const REPORT_TYPE = ReportType.LogYardVehicleMoves;
const TEMPLATE_NAME = 'log_yard_vehicle_moves';

export function useVehicleReport(): SILogReport {
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);

  async function fetchDataFromServer(config: ReportServerConfig): Promise<ReportData | null> {
    const httpClient = useHttpClient();
    const headers = httpClient.defaultHeaders();

    // Get date range from filter values or use default
    const filterValues = config.filterValues as
      | { from: string; to: string; vehicleId: string }
      | undefined;
    if (!filterValues?.from || !filterValues?.to || !filterValues?.vehicleId) {
      throw new Error('Filter values "from", "to" and "vehicleId" are required');
    }

    const body: LogYardVehicleMovesRequest = {
      from: filterValues.from,
      to: filterValues.to,
      vehicleId: filterValues.vehicleId,
    };

    const request = new Request(httpClient.siteUrl(`report/${REPORT_TYPE}`), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const response = await httpClient.execute(request);

    if (response.ok) {
      const reportDataFromServer = (await response.json()) as VehicleMoveCycle[];

      jsreport.serverUrl = config.reportUrl;
      if (config.authorization) {
        jsreport.headers['Authorization'] = config.authorization;
      }

      const darkMode = useDarkMode();
      const { enabled } = storeToRefs(darkMode);
      const themeStore = useThemeStore();
      const theme = themeStore.currentTheme;

      const reportData: ReportData = {
        darkMode: enabled.value,
        tiYardTheme: theme === 'TiYard',
        totalMoves: 0,
        totalLogs: 0,
        totalVolume: '',
        totalTime: 0,
        moves: [],
      };

      let totalMoves = 0;
      let totalLogs = 0;
      let totalVolume = 0.0;
      let totalTime = 0;

      for (const move of reportDataFromServer) {
        totalMoves += 1;
        totalLogs += move.numberOfLogs;
        totalVolume += move.volume;

        var fromDate = new Date(move.fromServerTimestamp);
        var toDate = new Date(move.toServerTimestamp);
        var timeSpent = Math.round((toDate.getTime() - fromDate.getTime()) / 1000); // in seconds
        totalTime += timeSpent;

        reportData.moves.push({
          from: move.fromStorageAreaName,
          pickupDateTime: fromDate.toLocaleString(languageStore.language.code, {
            dateStyle: 'short',
            timeStyle: 'short',
          }),
          to: move.toStorageAreaName,
          dropOffDateTime: toDate.toLocaleString(languageStore.language.code, {
            dateStyle: 'short',
            timeStyle: 'short',
          }),
          products: move.products,
          noOfLogs: move.numberOfLogs,
          volume: move.volume.toLocaleString(languageStore.language.code, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }),
          timeSpent: timeSpent,
        });
      }

      reportData.totalLogs = totalLogs;
      reportData.totalVolume = totalVolume.toLocaleString(languageStore.language.code, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
      reportData.totalTime = totalTime;
      reportData.totalMoves = totalMoves;

      return reportData;
    }

    return null;
  }

  return {
    name: translations.value.reports.reportName(REPORT_TYPE),
    type: REPORT_TYPE,

    exportFormats: [
      {
        label: translations.value.reports.exportFormat.pdf,
        icon: 'pi pi-file-pdf',
        format: 'pdf',
      },
      {
        label: translations.value.reports.exportFormat.excel,
        icon: 'pi pi-file-excel',
        format: 'excel',
      },
    ],

    filterComponent: VehicleMoveReportFilter,

    async showReport(config: ReportServerConfig | null): Promise<string> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: 'html',
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          let url = await report.toDataURI();
          url = url.replace(';base64,', ';charset=utf-8;base64,');
          return url;
        } catch (error) {
          console.error('Error generating report:', error);
          return '';
        }
      }

      return '';
    },

    async exportReport(config: ReportServerConfig | null, format: string): Promise<boolean> {
      if (!config) {
        throw new Error('ReportServerConfig is required');
      }

      const reportData = await fetchDataFromServer(config);

      let recipe = 'html';
      let extension = '.txt';
      if (format === 'pdf') {
        recipe = 'chrome-pdf';
        extension = 'pdf';
      } else if (format === 'excel') {
        recipe = 'html-to-xlsx';
        extension = 'xlsx';
      }

      if (reportData) {
        try {
          const report = await jsreport.render({
            template: {
              name: TEMPLATE_NAME,
              recipe: recipe,
            },
            data: reportData,
            options: { localization: { language: languageStore.language.code } },
          });

          report.download(`${TEMPLATE_NAME}.${extension}`);
          return true;
        } catch (error) {
          console.error('Error exporting report:', error);
        }
      }

      return false;
    },
  };
}
